import React, { useEffect, useMemo, useState } from "react";
import Client from "../../models/client";
import { AnnounceModel, ridTypeOptions } from "./Announce";
import { applyColumnFilter, ColumnFilter, DEFAULT_FILTER_BY_TYPE, Filter, isFilterActive } from "./ColumnFilter";
import { DataTable, DataTableSortStatus } from "mantine-datatable";
import useSWR from "swr";
import { deleteAnnounce, FETCH_KEYS, fetcherWithAuth } from "../api";
import { notifications } from "@mantine/notifications";
import { ActionIcon, Box, Button, Group, Select, Text, Tooltip } from "@mantine/core";
import { CopyIcon, DownloadSimpleIcon, PencilSimpleIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";
import QRCode from "react-qr-code";
import { AnnounceEditUrlForm } from "./AnnounceEditUrlForm";
import { TablePagination } from "./TablePagination";
import { confirmDelete } from "../utils/confirm";

const TEXT_FILTER_FIELDS = ['note', 'page_type', 'rid2', 'rid3', 'sales_id', 'campaign_id']

function compareValues(a: unknown, b: unknown): number {
  const aEmpty = a === null || a === undefined || a === ''
  const bEmpty = b === null || b === undefined || b === ''

  if (aEmpty && bEmpty) return 0
  if (aEmpty) return 1
  if (bEmpty) return -1
  if (typeof a === 'number' && typeof b === 'number') return a - b

  return String(a).localeCompare(String(b), 'ja')
}

type Props = {
  client: Client
}

export const AnnounceUrlList = ({ client }: Props) => {
  const [targetAnnounce, setTargetAnnounce] = useState<AnnounceModel | null>(null)
  const [modalState, setModalState] = useState(false)

  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [filters, setFilters] = useState<Record<string, Filter>>({})
  const [ridFilter, setRidFilter] = useState<string>('all')
  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<AnnounceModel> | null>(null)

  const baseUrl = window.location.origin + '/' + client.url_name
  const buildUrl = (url: string) => baseUrl + url

  const { data, isLoading, mutate } = useSWR<AnnounceModel[]>(
    FETCH_KEYS.announces(client.id),
    fetcherWithAuth,
    { revalidateOnFocus: false },
  )

  const ridFilterData = useMemo(
    () => [
      { value: 'all', label: '全て' },
      ...ridTypeOptions.map((o) => ({ value: o.value, label: `${o.label}（${o.value}）` })),
    ],
    [],
  )

  const qrid = (id: number) => `qrimg${id}`

  const handleCopy = (url: string) => {
    navigator.clipboard.writeText(url)
    notifications.show({ message: 'URLをコピーしました', color: 'teal' })
  }

  const handleDownload = (id: number) => {
    const svg = document.getElementById(qrid(id))
    if (!svg) return

    const svgData = new XMLSerializer().serializeToString(svg)
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const img = new Image()
    img.onload = function () {
      canvas.width = img.width
      canvas.height = img.height
      ctx.drawImage(img, 0, 0)

      const pngFile = canvas.toDataURL('image/png')
      const downloadLink = document.createElement('a')
      downloadLink.download = 'qrcode'
      downloadLink.href = pngFile
      downloadLink.click()
    }

    img.src = 'data:image/svg+xml;base64,' + btoa(svgData)
  }

  const handleClose = () => setModalState(false)

  const handleClickAddButton = () => {
    setTargetAnnounce(null)
    setModalState(true)
  }

  const handleClickEdit = (announce: AnnounceModel) => {
    setTargetAnnounce(announce)
    setModalState(true)
  }

  const handleDelete = async (announce: AnnounceModel) => {
    if (!confirmDelete(`告知用URL（ID: ${announce.id}）`)) {
      return
    }
    try {
      await deleteAnnounce(client.id, announce.id)
      await mutate()
      notifications.show({ message: '告知用URLを削除しました', color: 'teal' })
    } catch (e) {
      console.error('告知用URLの削除に失敗しました', e)
      notifications.show({ message: '告知用URLの削除に失敗しました', color: 'red' })
    }
  }

  const filteredRows = useMemo(() => {
    const rows = data ?? []
    return rows.filter((row) => {
      if (ridFilter !== 'all' && row.rid !== ridFilter) return false

      for (const field of TEXT_FILTER_FIELDS) {
        const f = filters[field]
        if (f && isFilterActive(f)) {
          if (!applyColumnFilter((row as Record<string, unknown>)[field], f, 'string')) {
            return false
          }
        }
      }

      return true
    })
  }, [data, filters, ridFilter])

  const sortedRows = useMemo(() => {
    if (!sortStatus) return filteredRows

    const { columnAccessor, direction } = sortStatus
    const sign = direction === 'desc' ? -1 : 1
    return [...filteredRows].sort(
      (a, b) =>
        sign *
        compareValues(
          (a as Record<string, unknown>)[columnAccessor as string],
          (b as Record<string, unknown>)[columnAccessor as string],
        ),
    )
  }, [filteredRows, sortStatus])

  const totalCount = sortedRows.length
  const totalPages = Math.max(1, Math.ceil(totalCount / perPage))

  const pagedRows = useMemo(() => {
    const from = (page - 1) * perPage
    return sortedRows.slice(from, from + perPage)
  }, [sortedRows, page, perPage])

  useEffect(() => {
    if (page > totalPages) setPage(totalPages)
  }, [page, totalPages])

  useEffect(() => {
    setPage(1)
  }, [filters, ridFilter])

  const columns = useMemo(() => {
    const textColumn = (accessor: string, title: string) => ({
      accessor,
      title,
      sortable: true,
      filter: (
        <ColumnFilter
          filter={filters[accessor] ?? DEFAULT_FILTER_BY_TYPE.string}
          onChange={(f: Filter) => setFilters((prev) => ({ ...prev, [accessor]: f }))}
          dataType="string"
        />
      ),
      filtering: isFilterActive(filters[accessor])
    })

    return [
      { accessor: 'id', title: 'ID', sortable: true, width: 70 },
      {
        accessor: 'note',
        title: 'メモ',
        sortable: true,
        cellsStyle: (): React.CSSProperties => ({ whiteSpace: 'pre-wrap' }),
        filter: (
          <ColumnFilter
            filter={filters.note ?? DEFAULT_FILTER_BY_TYPE.string}
            onChange={(f: Filter) => setFilters((prev) => ({ ...prev, note: f }))}
            dataType="string"
          />
        ),
        filtering: isFilterActive(filters.note),
      },
      {
        accessor: 'url',
        title: 'URL',
        sortable: true,
        ellipsis: true,
        width: 400,
        render: (r: AnnounceModel) => (
          <Group gap={4} wrap="nowrap">
            <Text fz={14} truncate="end" title={buildUrl(r.url)} style={{ flex: 1, minWidth: 0 }}>
              {buildUrl(r.url)}
            </Text>
            <Tooltip label="URLをコピー">
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={() => handleCopy(buildUrl(r.url))}
              >
                <CopyIcon size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        ),
      },
      textColumn('page_type', 'type'),
      {
        accessor: 'rid',
        title: 'rid',
        sortable: true,
        filter: (
          <Select
            data={ridFilterData}
            value={ridFilter}
            onChange={(v) => setRidFilter(v ?? 'all')}
            allowDeselect={false}
            comboboxProps={{ withinPortal: false }}
            checkIconPosition="right"
            searchable
          />
        ),
        filtering: ridFilter !== 'all',
      },
      textColumn('rid2', 'rid2'),
      textColumn('rid3', 'rid3'),
      textColumn('sales_id', 'sales_id'),
      textColumn('campaign_id', 'campaign_id'),
      {
        accessor: 'qrdl',
        title: 'QRDL',
        width: 80,
        textAlign: 'center' as const,
        render: (r: AnnounceModel) => (
          <>
            <Tooltip label="QR画像をダウンロード">
              <ActionIcon variant="subtle" color="gray" onClick={() => handleDownload(r.id)}>
                <DownloadSimpleIcon size={16} />
              </ActionIcon>
            </Tooltip>
            <QRCode
              style={{ display: 'none' }}
              id={qrid(r.id)}
              value={encodeURI(buildUrl(r.url))}
              size={180}
            />
          </>
        ),
      },
      {
        accessor: 'edit',
        title: '編集',
        width: 80,
        textAlign: 'center' as const,
        render: (r: AnnounceModel) => (
          <Tooltip label="編集">
            <ActionIcon variant="subtle" color="gray" onClick={() => handleClickEdit(r)}>
              <PencilSimpleIcon size={16} />
            </ActionIcon>
          </Tooltip>
        ),
      },
      {
        accessor: 'delete',
        title: '削除',
        width: 80,
        textAlign: 'center' as const,
        render: (r: AnnounceModel) => (
          <Tooltip label="削除">
            <ActionIcon variant="subtle" color="red" onClick={() => handleDelete(r)}>
              <TrashIcon size={16} />
            </ActionIcon>
          </Tooltip>
        ),
      },
    ]
  }, [filters, ridFilter, ridFilterData])

  return (
    <Box mt={60}>
      <Group mb={16}>
        <Button
          variant="filled"
          radius="xl"
          onClick={handleClickAddButton}
          leftSection={<PlusIcon size={16} weight="bold" />}
        >
          新規作成
        </Button>
      </Group>

      <Box
        style={{
          border: '1px solid #0000001A',
          borderRadius: '8px',
          overflow: 'hidden',
          width: '100%',
        }}
      >
        <DataTable
          withColumnBorders
          fetching={isLoading}
          records={pagedRows}
          idAccessor="id"
          noRecordsText="告知用URLが登録されていません"
          mih={pagedRows.length === 0 ? 450 : undefined}
          styles={{
            root: { border: 'none' },
            header: { backgroundColor: '#00000008' },
          }}
          columns={columns}
          sortStatus={sortStatus ?? { columnAccessor: '', direction: 'asc' }}
          onSortStatusChange={setSortStatus}
        />

        <TablePagination
          page={page}
          onChangePage={setPage}
          perPage={perPage}
          onChangePerPage={(v) => { setPerPage(v); setPage(1) }}
          totalPages={totalPages}
          isEmpty={pagedRows.length === 0}
        />
      </Box>

      <AnnounceEditUrlForm
        opened={modalState}
        onClose={handleClose}
        announce={targetAnnounce}
        client={client}
        onSaved={mutate}
      />
    </Box>
  )
}
